import { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import CommonModal from "components/common/CommonModal";
import addPhoto from "assets/images/imgs/img_license_enter.png";
import { submitLicenseImg, getLicenseList } from "api/user";
import useRefreshCallback from "hooks/useRefreshCallback";
import MediaViewer from "components/common/MediaViewer";
import Badge from "components/common/Badge";
import {
    LICENSE_SPORTS,
    getLicenseCompanies,
    getLicenseLevels,
} from "constants/licenses";

const VerifyLicense = () => {
    const { type } = useParams(); //student, instructor

    const [showModal, setShowModal] = useState(false);
    const [licenseList, setLicenseList] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [licenseSport, setLicenseSport] = useState("");
    const [licenseCompany, setLicenseCompany] = useState("");
    const [licenseSorts, setLicenseSorts] = useState([]);
    const [licenseLevel, setLicenseLevel] = useState("");
    const [viewerIndex, setViewerIndex] = useState(null);
    const [isInstructor, setIsInstructor] = useState(type === "instructor");

    const fileInputRef = useRef(null);

    // 뷰어는 파일이 있는 자격증만 보여준다. 선택 index 도 이 걸러진 배열 기준이어야
    // media·title 이 같은 항목을 가리킨다(원본 index 를 쓰면 누락 항목만큼 어긋난다).
    const viewableLicenses = useMemo(
        () => (licenseList || []).filter((l) => l.certificate_file),
        [licenseList],
    );

    const openViewer = (license) => {
        const index = viewableLicenses.indexOf(license);
        // 파일이 없는 항목은 열 것이 없다.
        if (index < 0) return;
        setViewerIndex(index);
    };

    useRefreshCallback(() => {
        return getLicenseList().then((res) => {
            setLicenseList(res);
        });
    });

    useEffect(() => {
        getLicenseList().then((res) => {
            setLicenseList(res);
        });
    }, []);

    // 스포츠/강사여부에 해당하는 단체만 노출한다.
    const companyOptions = licenseSport
        ? getLicenseCompanies(licenseSport, isInstructor)
        : [];

    const handleSportChange = (event) => {
        setLicenseSport(event.target.value);
        // 스포츠 변경 시 하위 선택(단체/레벨) 초기화
        setLicenseCompany("");
        setLicenseSorts([]);
        setLicenseLevel("");
    };

    const makeLicenseList = (
        company,
        instructor = isInstructor,
        sport = licenseSport
    ) => {
        setLicenseCompany(company);
        setLicenseSorts(getLicenseLevels(company, instructor, sport));
        setLicenseLevel("");
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        const imageUrl = URL.createObjectURL(file);

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("이미지 파일만 업로드 가능합니다.");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert("파일 크기는 5MB 이하여야 합니다.");
            return;
        }

        setSelectedFile(file);
        setImageUrl(imageUrl);
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewImage(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleImageClick();
        }
    };

    const handleUpload = async () => {
        // 중복 제출 방지: 이미 업로드 중이면 무시한다.
        if (isUploading) return;
        if (!selectedFile) {
            alert("업로드할 파일을 선택해주세요.");
            return;
        }
        setIsUploading(true);

        const formData = new FormData();
        formData.append("certificate_file", selectedFile);
        formData.append(
            "certificate_name",
            licenseCompany + " " + licenseLevel
        );

        submitLicenseImg(formData)
            .then((res) => {
                if (res.status === 201) {
                    setSelectedFile(null);
                    setPreviewImage(null);
                    setShowModal(false);

                    setLicenseList((prev) => [
                        ...prev,
                        {
                            id: Date.now(),
                            name: selectedFile.name,
                            image: previewImage,
                            status: "pending",
                        },
                    ]);
                } else {
                    alert("업로드에 실패했습니다. 다시 시도해주세요.");
                }
            })
            .finally(() => {
                setIsUploading(false);
                setShowModal(false);
            });
    };

    const handleInstructorToggle = (event) => {
        const checked = event.target.checked;
        setIsInstructor(checked);
        // 강사/수강생 전환 시 노출 단체가 달라지므로 단체/레벨 재선택 유도
        setLicenseCompany("");
        setLicenseSorts([]);
        setLicenseLevel("");
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        setPreviewImage(null);
        setImageUrl(null);
        setLicenseSport("");
        setLicenseCompany("");
        setLicenseLevel("");
        setLicenseSorts([]);
        setIsInstructor(type === "instructor");

        // 파일 입력 필드 초기화
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };



    return (
        <>
            <div>
                <SubHeader
                    title={
                        type === "student"
                            ? "수강생 자격증 등록"
                            : "강사 자격증 등록"
                    }
                />
                <div className="verify-license-wrap">
                    {licenseList && licenseList?.length > 0 ? (
                        <div className="license-card-wrap">
                            {licenseList.map((license) => (
                                <div key={license.id} className="license-card">
                                    <img
                                        src={license.certificate_file}
                                        alt={license.certificate_name}
                                        width={100}
                                        height={150}
                                        style={{ cursor: "zoom-in" }}
                                        onClick={() => openViewer(license)}
                                        role="button"
                                    />
                                    <div className="license-card-info">
                                        <p>{license.certificate_name}</p>
                                        <p>{license.is_approved && license.uploaded_at?.split("T")[0].replaceAll("-", ".")} 
                                        { license.rejection_reason && license.rejection_reason.length > 0 && " 반려사유 : " + license.rejection_reason}
                                        </p>
                                    </div>
                                    <Badge text={license.is_approved
                                            ? "승인"
                                            : license.rejection_reason && license.rejection_reason.length > 0 ? '반려': "검토중"} 
                                            type='soft'
                                            color={license.is_approved
                                              ? "purple"
                                              : license.rejection_reason && license.rejection_reason.length > 0 ? 'red': "orange"}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="license-noti-wrap">
                            자격증을 등록해주세요
                        </div>
                    )}
                    <button
                        className="btn btn-border-8 btn-large btn-outline-purple500"
                        onClick={() => {
                            // 모달 열기 전에 모든 상태 초기화
                            setSelectedFile(null);
                            setPreviewImage(null);
                            setImageUrl(null);
                            setLicenseSport("");
                            setLicenseCompany("");
                            setLicenseLevel("");
                            setLicenseSorts([]);
                            setIsInstructor(type === "instructor");

                            // 파일 입력 필드 초기화
                            if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                            }

                            setShowModal(true);
                        }}
                    >
                        자격증 추가
                    </button>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
            />

            <CommonModal
                isOpen={showModal}
                onRequestClose={handleCloseModal}
                title="자격증 등록"
                className={"verify-license-modal"}
            >
                <div className="input-form select-input-wrap">
                    <select
                        className="input"
                        value={licenseSport}
                        onChange={handleSportChange}
                    >
                        <option value="">종목 선택</option>
                        {LICENSE_SPORTS.map((sport) => (
                            <option key={sport.value} value={sport.value}>
                                {sport.label}
                            </option>
                        ))}
                    </select>
                </div>
                <label className="instructor-check">
                    <input
                        type="checkbox"
                        checked={isInstructor}
                        onChange={handleInstructorToggle}
                    />
                    <span>강사인 경우 체크해주세요.</span>
                </label>
                <div className="input-form select-input-wrap">
                    <select
                        className="input"
                        value={licenseCompany}
                        onChange={(e) => makeLicenseList(e.target.value)}
                        disabled={!licenseSport}
                    >
                        <option value="">단체 선택</option>
                        {companyOptions.map((company) => (
                            <option key={company} value={company}>
                                {company}
                            </option>
                        ))}
                    </select>

                    <select
                        className="input"
                        value={licenseLevel}
                        onChange={(e) => setLicenseLevel(e.target.value)}
                        disabled={!licenseCompany}
                    >
                        <option value="">레벨</option>
                        {licenseSorts.map((sort) => (
                            <option key={sort} value={sort}>
                                {sort}
                            </option>
                        ))}
                    </select>
                </div>
                <div
                    className="upload-image-container"
                    onClick={handleImageClick}
                    onKeyDown={handleImageKeyDown}
                    tabIndex={0}
                    role="button"
                    aria-label="자격증 이미지 업로드"
                >
                    {previewImage ? (
                        <img
                            src={previewImage}
                            alt="선택된 자격증 이미지"
                            style={{ cursor: "pointer" }}
                        />
                    ) : (
                        <img
                            src={addPhoto}
                            alt="자격증 등록 이미지"
                            style={{ cursor: "pointer" }}
                        />
                    )}
                </div>

                <div className="noti-content">
                    <p className="title bold">확인해 주세요!</p>
                    <p>{` 자격증 등록은 순차적으로 진행됩니다.`}</p>
                    <p>
                        등록 확인은 <span>마이버클</span>에서 확인이 가능해요
                    </p>
                </div>
                <div className="btn-wrap-flex gap-10">
                    <button
                        className="btn btn-border-8 btn-large btn-purple200"
                        onClick={handleCloseModal}
                        disabled={isUploading}
                    >
                        취소
                    </button>
                    <button
                        className={`btn btn-border-8 btn-large ${
                            selectedFile === null || !licenseLevel || isUploading
                                ? "btn-disabled"
                                : "btn-purple500"
                        }`}
                        onClick={handleUpload}
                        disabled={
                            selectedFile === null || !licenseLevel || isUploading
                        }
                    >
                        {isUploading ? "등록중..." : "등록"}
                    </button>
                </div>
            </CommonModal>

            {viewerIndex !== null && (
                <MediaViewer
                    media={viewableLicenses.map((l) => ({ url: l.certificate_file, type: "image" }))}
                    initialIndex={viewerIndex}
                    title={viewableLicenses[viewerIndex]?.certificate_name}
                    onClose={() => setViewerIndex(null)}
                />
            )}
        </>
    );
};

export default VerifyLicense;
